/**
 * Aggregated dirty-state for the draft (workflow + connections).
 *
 * Every save-related surface needs the same question answered: "does the
 * draft have unsaved changes?". Centralising it here keeps the four
 * existing call sites (autosave loop, status badge, manual save button,
 * keyboard shortcut handler) in lockstep — if we ever add a third
 * dirty-tracked artifact (e.g. parameters), this is the only place to
 * update.
 */

import { useWorkflowStore } from '@/stores/workflowStore';
import { useConnectionStore } from '@/stores/connectionStore';

/** Reactive: subscribes to both stores' `isDirty` flags. */
export const useIsDraftDirty = (): boolean => {
  const workflowIsDirty = useWorkflowStore((s) => s.isDirty);
  const connectionsIsDirty = useConnectionStore((s) => s.isDirty);
  return workflowIsDirty || connectionsIsDirty;
};

/**
 * Non-reactive companion for event handlers that need the current value
 * without re-binding on every dirty toggle (e.g. global Ctrl+S listener).
 */
export const getIsDraftDirty = (): boolean =>
  useWorkflowStore.getState().isDirty || useConnectionStore.getState().isDirty;
